import React, { createContext, useContext, useState, useEffect } from 'react'
import { ChartPreferencesType, UserData } from '@/types/theme'
import { getScrollbarStyles, theme } from '@/lib/theme'

interface ThemeContextType {
  isDarkMode: boolean
  setIsDarkMode: (value: boolean) => void
  showInternalCharts: boolean
  setShowInternalCharts: (value: boolean) => void
  userData: UserData | null
  setUserData: (data: UserData | null) => void
  chartPreferences: ChartPreferencesType
  setChartPreferences: (preferences: Partial<ChartPreferencesType>) => void
  currentTheme: ReturnType<typeof theme>
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const defaultChartPreferences: ChartPreferencesType = {
  showLegend: true,
  showYAxis: true,
  smoothLines: true,
  autoAlignYAxis: true,
  showTooltip: true,
  showXAxis: true,
  showDataLabels: false,
  showGridLines: true,
  zoomEnabled: true,
  sliderEnabled: false,
  showTable: true,
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userData, setUserDataState] = useState<UserData | null>(() => {
    try {
      const stored = localStorage.getItem('userData')
      return stored ? (JSON.parse(stored) as UserData) : null
    } catch {
      return null
    }
  })
  const [isDarkMode, setIsDarkModeState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('userData')
      if (stored) return (JSON.parse(stored) as UserData).is_dark_mode ?? false
    } catch {}
    try {
      return localStorage.getItem('theme') === 'dark'
    } catch {}
    return false
  })
  const [showInternalCharts, setShowInternalChartsState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('userData')
      if (stored) return (JSON.parse(stored) as UserData).show_internal_charts ?? true
    } catch {}
    return true
  })
  const [chartPreferences, setChartPreferencesState] =
    useState<ChartPreferencesType>(defaultChartPreferences)

  const currentTheme = theme(isDarkMode)

  useEffect(() => {
    // Apply theme to document
    const root = document.documentElement
    if (isDarkMode) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }

    // Apply scrollbar styles
    const style = document.createElement('style')
    style.textContent = getScrollbarStyles(currentTheme)
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [isDarkMode, currentTheme])

  const setUserData = (data: UserData | null) => {
    setUserDataState(data)
    if (data) {
      try {
        localStorage.setItem('userData', JSON.stringify(data))
        setIsDarkModeState(data.is_dark_mode)
        setShowInternalChartsState(data.show_internal_charts)
      } catch (error) {
        console.error('Error saving user data:', error)
      }
    }
  }

  const setIsDarkMode = (value: boolean) => {
    setIsDarkModeState(value)
    try {
      localStorage.setItem('theme', value ? 'dark' : 'light')
    } catch {}
    if (userData) {
      const updatedData = { ...userData, is_dark_mode: value }
      setUserData(updatedData)
    }
  }

  const setShowInternalCharts = (value: boolean) => {
    setShowInternalChartsState(value)
    if (userData) {
      const updatedData = { ...userData, show_internal_charts: value }
      setUserData(updatedData)
    }
  }

  const setChartPreferences = (preferences: Partial<ChartPreferencesType>) => {
    const newPreferences = { ...chartPreferences, ...preferences }
    setChartPreferencesState(newPreferences)
    if (userData) {
      const updatedData = { ...userData, chartPreferences: newPreferences }
      setUserData(updatedData)
    }
  }

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode,
        setIsDarkMode,
        showInternalCharts,
        setShowInternalCharts,
        userData,
        setUserData,
        chartPreferences,
        setChartPreferences,
        currentTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}
